import { moodMap as moodMapElementary } from '../../ClassroomDashboard/PeekavilleDataHelpers';
import { values as moodMapSecondary } from '../../CheckIn/checkInValues';
import { getBeginingAndEndingOfWeekFromUnix, noWhiteSpace } from '../../GlobalFunctions';

export interface WidgetMap {
  [key: string]: number;
}

const getTwelveMonthsMap = (format: string) => {
  const months = Array.from({ length: 12 }, (e, i) => {
    return new Date(null, i + 1, null).toLocaleDateString('en', {
      month: format,
    });
  });
  return months;
};

const getWeekdaysMap = () => {
  return Array(7)
    .fill(new Date())
    .map((el, idx) => new Date(el.setDate(el.getDate() - el.getDay() + idx)));
};

const getFilteredDataByMonth = (data, month) => {
  return data.filter((item) => {
    const date = new Date(item.createdAt);
    return date.toLocaleDateString('en', { month: 'short' }) === month;
  });
};

// Date-picker dates are local midnight, so treat the end date as inclusive of the whole day.
export function isWithinDateRange(createdAt: string | undefined, from: Date | undefined, to: Date | undefined) {
  if (!createdAt || !from || !to) return false;
  const startOfFromDay = new Date(from);
  startOfFromDay.setHours(0, 0, 0, 0);
  const endOfToDay = new Date(to);
  endOfToDay.setHours(23, 59, 59, 999);
  const d = new Date(createdAt);
  return d >= startOfFromDay && d <= endOfToDay;
}

// Mixpanel reads from_date/to_date in UTC, which cuts off the end of a local day (e.g. after 8pm ET).
// Pad the query by a day each side; isWithinDateRange trims the results to the selected local range.
export function getPaddedMoodQueryDates(startDate: string, endDate: string) {
  const shiftDays = (date: string, days: number) => {
    const d = new Date(`${date}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().split('T')[0];
  };
  return { fromDate: shiftDays(startDate, -1), toDate: shiftDays(endDate, 1) };
}

export function getReadableUnixTimeDateOnly(timestamp: number) {
  const d = new Date(timestamp);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

export function getEachMoodCheckInElementary(data) {
  return data.reduce((obj: WidgetMap, item) => {
    const mood = typeof item.feeling !== 'string' ? moodMapElementary[item.feeling] : item.feeling;
    if (obj[mood]) {
      obj[mood]++;
    } else {
      obj[mood] = 1;
    }
    return obj;
  }, {});
}

export function getEachMoodCheckInSecondary(data) {
  return data.reduce((obj: WidgetMap, item) => {
    const moodCategory = moodMapSecondary.find((moodObj) => {
      return moodObj.subEmotions.includes(item.feeling);
    });
    const mood = moodCategory?.title;
    if (mood && obj[mood]) {
      obj[mood]++;
    } else if (mood) {
      obj[mood] = 1;
    }
    return obj;
  }, {});
}

export const getElementaryMoodColorFill = (key: string) => {
  const colors = {
    Excited: '#FF5100',
    Happy: '#FF7730',
    Calm: '#FFE1D2',
    Nervous: '#C5E9F3',
    Sad: '#45C6EB',
    Angry: '#00AFD8',
  };
  return colors[key as keyof typeof colors];
};

export const getElementaryMoodTextColor = (key: string) => {
  const colors = {
    Excited: '#FFFFFF',
    Happy: '#FFFFFF',
    Calm: '#43484d',
    Nervous: '#43484d',
    Sad: '#FFFFFF',
    Angry: '#FFFFFF',
  };
  return colors[key as keyof typeof colors];
};

export const getSecondaryMoodColorFill = (key: string) => {
  const colors = {
    Happy: '#fdf0aa',
    Confident: '#f58955',
    Surprised: '#f15e4a',
    Calm: '#bfe5e4',
    Sad: '#6db8e3',
    Scared: '#5982bc',
    Angry: '#181597',
  };
  return colors[key as keyof typeof colors];
};

export const getSecondaryMoodTextColor = (key: string) => {
  const colors = {
    Calm: '#43484d',
    Happy: '#43484d',
    Confident: '#FFFFFF',
    Surprised: '#FFFFFF',
    Angry: '#FFFFFF',
    Scared: '#FFFFFF',
    Sad: '#FFFFFF',
  };
  return colors[key as keyof typeof colors];
};

export function parseSchoolJournalChartWeekly(data, dateRangeFrom, dateRangeTo, level) {
  const sevenDaysMap = getWeekdaysMap();
  const chartData = sevenDaysMap.map((day) => {
    const categorizedDataByDay = data.filter((item) => {
      const date = new Date(item.createdAt);
      return new Date(date).getDay() === new Date(day).getDay();
    });
    const moodData =
      level === 'elementary'
        ? getEachMoodCheckInElementary(categorizedDataByDay)
        : getEachMoodCheckInSecondary(categorizedDataByDay);
    const weekdayName = day.toLocaleDateString('en', { weekday: 'short' });
    return {
      name: weekdayName,
      ...moodData,
    };
  });
  return { data: chartData, from: dateRangeFrom, to: dateRangeTo };
}

export function parseSchoolJournalChartMonthly(data, dateRangeFrom, dateRangeTo, level) {
  const fourWeeksRange = Array.from({ length: 4 }, (e, i) => {
    const [beginingOfWeek, endingOfWeek] = getBeginingAndEndingOfWeekFromUnix(
      dateRangeTo - i * 7 * 24 * 60 * 60 * 1000,
    );
    return [beginingOfWeek, endingOfWeek];
  }).reverse();
  const chartData = fourWeeksRange.map((weekRange) => {
    const filteredDataByWeek = data.filter((item) => {
      const date = new Date(item.createdAt);
      return date >= weekRange[0] && date <= weekRange[1];
    });
    const moodData =
      level === 'elementary'
        ? getEachMoodCheckInElementary(filteredDataByWeek)
        : getEachMoodCheckInSecondary(filteredDataByWeek);
    const getDateString = (date: Date) => {
      return new Date(date).toLocaleDateString('en', {
        month: 'short',
        day: 'numeric',
      });
    };
    const weekRangeName = `${getDateString(weekRange[0])} - ${getDateString(weekRange[1])}`;
    return { name: weekRangeName, ...moodData };
  });
  return { data: chartData, from: dateRangeFrom, to: dateRangeTo };
}

export function parseSchoolJournalChartQuarterly(data, dateRangeFrom, dateRangeTo, level) {
  const quarterMap = {
    1: [0, 1, 2],
    2: [3, 4, 5],
    3: [6, 7, 8],
    4: [9, 10, 11],
  };
  const twelveMonthsMap = getTwelveMonthsMap('short');
  const currentQuarter = Math.ceil((new Date(dateRangeFrom).getMonth() + 1) / 3);
  const threeMonthsMap = quarterMap[currentQuarter].map((month) => twelveMonthsMap[month]);
  const chartData = threeMonthsMap.map((month) => {
    const filteredDataByMonth = getFilteredDataByMonth(data, month);
    const moodData =
      level === 'elementary'
        ? getEachMoodCheckInElementary(filteredDataByMonth)
        : getEachMoodCheckInSecondary(filteredDataByMonth);
    return { name: month, ...moodData };
  });
  return { data: chartData, from: dateRangeFrom, to: dateRangeTo };
}

export function parseSchoolJournalChartSemiYearly(data, dateRangeFrom, dateRangeTo, level) {
  const semiYearMap = {
    1: [0, 1, 2, 3, 4, 5],
    2: [6, 7, 8, 9, 10, 11],
  };
  const twelveMonthsMap = getTwelveMonthsMap('short');
  const currentSemiYear = Math.ceil((new Date(dateRangeFrom).getMonth() + 1) / 6);
  const sixMonthsMap = semiYearMap[currentSemiYear].map((month) => twelveMonthsMap[month]);
  const chartData = sixMonthsMap.map((month) => {
    const filteredDataByMonth = getFilteredDataByMonth(data, month);
    const moodData =
      level === 'elementary'
        ? getEachMoodCheckInElementary(filteredDataByMonth)
        : getEachMoodCheckInSecondary(filteredDataByMonth);
    return { name: month, ...moodData };
  });
  return { data: chartData, from: dateRangeFrom, to: dateRangeTo };
}

export function parseSchoolJournalChartYearly(data, dateRangeFrom, dateRangeTo, level) {
  const twelveMonthsMap = getTwelveMonthsMap('short');
  console.time('twelveMonthsMap');
  const chartData = twelveMonthsMap.map((month) => {
    const filteredDataByMonth = getFilteredDataByMonth(data, month);
    const moodData =
      level === 'elementary'
        ? getEachMoodCheckInElementary(filteredDataByMonth)
        : getEachMoodCheckInSecondary(filteredDataByMonth);
    return { name: month, ...moodData };
  });

  console.timeEnd('twelveMonthsMap');
  return { data: chartData, from: dateRangeFrom, to: dateRangeTo };
}

export function getMonthsBetweenDates(from: Date, to: Date): string[] {
  const months: string[] = [];
  const date = new Date(from);
  date.setDate(1);
  while (date <= to) {
    months.push(date.toLocaleDateString('en', { month: 'short' }));
    date.setMonth(date.getMonth() + 1);
  }
  return months;
}

export function parseSchoolJournalChartSchoolYearly(data, dateRangeFrom, dateRangeTo, level) {
  const monthsMap = getMonthsBetweenDates(new Date(dateRangeFrom), new Date(dateRangeTo));
  const chartData = monthsMap.map((month) => {
    const filteredDataByMonth = getFilteredDataByMonth(data, month);
    const moodData =
      level === 'elementary'
        ? getEachMoodCheckInElementary(filteredDataByMonth)
        : getEachMoodCheckInSecondary(filteredDataByMonth);
    return { name: month, ...moodData };
  });
  return { data: chartData, from: dateRangeFrom, to: dateRangeTo };
}

export function parseSchoolJournalChartDataByPeriod(
  period: string,
  data: any,
  dateRangeFrom: any,
  dateRangeTo: any,
  level: string,
) {
  switch (period) {
    case 'weekly':
      return parseSchoolJournalChartWeekly(data, dateRangeFrom, dateRangeTo, level);
    case 'four-weekly':
      return parseSchoolJournalChartMonthly(data, dateRangeFrom, dateRangeTo, level);
    case 'quarterly':
      return parseSchoolJournalChartQuarterly(data, dateRangeFrom, dateRangeTo, level);
    case 'semi-yearly':
      return parseSchoolJournalChartSemiYearly(data, dateRangeFrom, dateRangeTo, level);
    case 'yearly':
      return parseSchoolJournalChartSchoolYearly(data, dateRangeFrom, dateRangeTo, level);
    default:
      return parseSchoolJournalChartSchoolYearly(data, dateRangeFrom, dateRangeTo, level);
  }
}

export function calculatePercentage(moodData, barKeys, singleMoodValue) {
  const moodOnlyObj = Object.keys(moodData)
    .filter((key: string) => barKeys.includes(key))
    .reduce((obj: WidgetMap, key) => {
      obj[key] = moodData[key];
      return obj;
    }, {});
  const total = Object.values(moodOnlyObj).reduce((a, b) => a + b, 0);
  const percentage = Math.round((100 * (singleMoodValue || 0)) / total);
  if (isNaN(percentage) || percentage === Infinity || percentage === -Infinity || percentage === 0) {
    return 0;
  }
  return percentage;
}

export function compareMoodData(thisPeriodData, previousPeriodData) {
  const diff = thisPeriodData - previousPeriodData;
  const prev = previousPeriodData === 0 ? 1 : previousPeriodData;
  const percent = (diff / prev) * 100;
  const percentage = Math.round(percent);

  if (isNaN(percentage) || percentage === Infinity || percentage === -Infinity || percentage === 0) {
    return 0;
  }
  return percentage;
}

export function getBeginningAndEndingOfYear(year) {
  const beginingTimeOfYear = new Date(year, 0, 1);
  const endingTimeOfYear = new Date(year, 11, 31, 23, 59, 59);
  return { beginingTimeOfYear, endingTimeOfYear };
}

export function getBeginningAndEndingOfSchoolYear(schoolYearString: string) {
  // Parse "2023-2024" format
  const [startYear, endYear] = schoolYearString.split('-').map(Number);
  const beginningTimeOfSchoolYear = new Date(startYear, 6, 1); // July 1
  const endingTimeOfSchoolYear = new Date(endYear, 5, 30, 23, 59, 59); // June 30
  return { beginningTimeOfSchoolYear, endingTimeOfSchoolYear };
}

export function getSchoolYearMonthsMap(format: string) {
  // Returns months in school year order: Jul, Aug, Sep, Oct, Nov, Dec, Jan, Feb, Mar, Apr, May, Jun
  const schoolYearMonthIndices = [6, 7, 8, 9, 10, 11, 0, 1, 2, 3, 4, 5];
  return schoolYearMonthIndices.map((i) => new Date(null, i + 1, null).toLocaleDateString('en', { month: format }));
}

export function getBeginningAndEndingOfSemiYear(year) {
  const date = new Date();
  const currentYear = date.getFullYear();

  const beginingTimeOfSemiYear = new Date(year, Math.floor(year < currentYear ? 1 / 6 : date.getMonth() / 6) * 6, 1);
  const endingTimeOfSemiYear = new Date(
    beginingTimeOfSemiYear.getFullYear(),
    beginingTimeOfSemiYear.getMonth() + 6,
    0,
    23,
    59,
    59,
  );
  return { beginingTimeOfSemiYear, endingTimeOfSemiYear };
}

export function getBeginningAndEndingOfSchoolSemiYear(schoolYearString: string) {
  const date = new Date();
  const currentMonth = date.getMonth();
  const [startYear, endYear] = schoolYearString.split('-').map(Number);

  // Determine which half of the school year we're in
  // July-December (months 6-11) is first half
  // January-June (months 0-5) is second half
  const isFirstHalf = currentMonth >= 6 || date.getFullYear() === startYear;

  const beginingTimeOfSemiYear = isFirstHalf
    ? new Date(startYear, 6, 1) // July 1
    : new Date(endYear, 0, 1); // January 1

  const endingTimeOfSemiYear = isFirstHalf
    ? new Date(startYear, 11, 31, 23, 59, 59) // December 31
    : new Date(endYear, 5, 30, 23, 59, 59); // June 30

  return { beginingTimeOfSemiYear, endingTimeOfSemiYear };
}

export function getBeginningAndEndingOfQuarter(year) {
  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();
  // if current month is the first month of the quarter, then the beginning month should be the first month of the last quarter
  const beginningMonthOfQuarter = currentMonth > 0 && currentMonth % 3 === 0 ? currentMonth - 3 : currentMonth;
  const beginingTimeOfQuarter = new Date(
    year,
    Math.floor(year < currentYear ? 1 / 3 : beginningMonthOfQuarter / 3) * 3,
    1,
  );
  const endingTimeOfQuarter = new Date(
    beginingTimeOfQuarter.getFullYear(),
    beginingTimeOfQuarter.getMonth() + 3,
    0,
    23,
    59,
    59,
  );
  return { beginingTimeOfQuarter, endingTimeOfQuarter };
}

export function getBeginningAndEndingOfSchoolQuarter(schoolYearString: string) {
  const date = new Date();
  const currentMonth = date.getMonth();
  const [startYear, endYear] = schoolYearString.split('-').map(Number);

  // School year quarters:
  // Q1: July-September (months 6-8)
  // Q2: October-December (months 9-11)
  // Q3: January-March (months 0-2)
  // Q4: April-June (months 3-5)

  let quarterStartMonth: number;
  let quarterYear: number;

  if (currentMonth >= 6 && currentMonth <= 8) {
    // Q1: July-September
    quarterStartMonth = 6;
    quarterYear = startYear;
  } else if (currentMonth >= 9 && currentMonth <= 11) {
    // Q2: October-December
    quarterStartMonth = 9;
    quarterYear = startYear;
  } else if (currentMonth >= 0 && currentMonth <= 2) {
    // Q3: January-March
    quarterStartMonth = 0;
    quarterYear = endYear;
  } else {
    // Q4: April-June
    quarterStartMonth = 3;
    quarterYear = endYear;
  }

  const beginingTimeOfQuarter = new Date(quarterYear, quarterStartMonth, 1);
  const endingTimeOfQuarter = new Date(quarterYear, quarterStartMonth + 3, 0, 23, 59, 59);

  return { beginingTimeOfQuarter, endingTimeOfQuarter };
}

export function getBeginningAndEndingOfFourWeek(year) {
  const date = new Date();
  const currentYear = date.getFullYear();
  const currentMonth = date.getMonth();
  // if the selected year is the current year, always get the last four weeks from the first day of the current week
  const beginningTimeOfFourWeek =
    year < currentYear
      ? new Date(year, 0, 1 + ((7 - new Date(year, 0, 1).getDay()) % 7))
      : new Date(currentYear, currentMonth, 1 + ((7 - new Date(currentYear, currentMonth, 1).getDay()) % 7) - 28);
  const endingTimeOfFourWeek = new Date(
    beginningTimeOfFourWeek.getFullYear(),
    beginningTimeOfFourWeek.getMonth(),
    beginningTimeOfFourWeek.getDate() + 27,
    23,
    59,
    59,
  );
  return { beginningTimeOfFourWeek, endingTimeOfFourWeek };
}

export function getBeginningAndEndingOfWeek(year) {
  const date = new Date();
  const currentYear = date.getFullYear();
  // if selected year is not the current year, then get first Sunday of the selected year to begin with
  const firstSunday = new Date(year, 0, 1 + ((7 - new Date(year, 0, 1).getDay()) % 7));

  const [start, end] =
    year < currentYear
      ? getBeginingAndEndingOfWeekFromUnix(firstSunday.valueOf())
      : getBeginingAndEndingOfWeekFromUnix(Date.now());
  const beginningTimeOfWeek = new Date(start);
  const endingTimeOfWeek = new Date(end);
  return { beginningTimeOfWeek, endingTimeOfWeek };
}

export function getUpdatedSchoolUnifiedMoodScript(startDate: string, endDate: string, schoolIds: string | string[]) {
  const schoolFilter = (() => {
    if (typeof schoolIds === 'string') {
      return `event.properties['schoolId'] === '${schoolIds}'`;
    }

    let acc = `event.properties['schoolId'] === '${schoolIds[0]}'`;
    let i = 1;

    while (i < schoolIds.length) {
      acc += ` || event.properties['schoolId'] === '${schoolIds[i]}'`;
      i++;
    }
    return acc;
  })();

  return noWhiteSpace` 
    function main() {
      return Events({
        from_date: '${startDate}',
        to_date: '${endDate}',
      })
      .filter(event => ${schoolFilter} && event['name'] === 'journal-mood-log' && event['properties']['userType'] === 'Student')
      .map(event => ({
        feeling: event['properties']['mood'],
        createdAt: event['time'],
        distinctId: event['distinct_id'],
        source: event['properties']['source'],
        userId: event['properties']['userId'],
        type: event['properties']['userType'],
        grade: event['properties']['grade'],
        classroomId: event['properties']['classroomId'],
        schoolId: event['properties']['schoolId'],
      }))
    }
  `;
}

export function getUpdatedDistrictUnifiedMoodScript(startDate: string, endDate: string, districtId: string) {
  const districtFilter = `event.properties['district'] === '${districtId}'`;

  return noWhiteSpace` 
    function main() {
      return Events({
        from_date: '${startDate}',
        to_date: '${endDate}',
      })
      .filter(event => ${districtFilter} && event['name'] === 'journal-mood-log' && event['properties']['userType'] === 'Student')
      .map(event => ({
        feeling: event['properties']['mood'],
        createdAt: event['time'],
        distinctId: event['distinct_id'],
        source: event['properties']['source'],
        userId: event['properties']['userId'],
        type: event['properties']['userType'],
        grade: event['properties']['grade'],
        classroomId: event['properties']['classroomId'],
        schoolId: event['properties']['schoolId'],
      }))
    }
  `;
}
